// AgentToLeadModal.tsx — confirm moving an agent back to Leads. The kept lead
// returns under its original id with all its history (admin-only page).

import React, { useState } from 'react';
import { ArrowLeftRight, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { C } from '../../../components/GlobalComponents/uiTokens';
import ProposalDialog, { Notice } from '../../Proposals/parts/ProposalDialog';
import { apiError } from '../../Proposals/parts/proposalTypes';
import { Agent, agentApi } from './agentTypes';

interface AgentToLeadModalProps {
  visible: boolean;
  agent: Agent | null;
  onHide: () => void;
  /** the lead is back — id lets the page open it */
  onMoved: (leadId: string, agentName: string) => void;
}

const AgentToLeadModal: React.FC<AgentToLeadModalProps> = ({ visible, agent, onHide, onMoved }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (busy) return;
    setError(null);
    onHide();
  };

  const confirm = async () => {
    if (!agent) return;
    setBusy(true);
    setError(null);
    try {
      const res = await agentApi.transferToLead(agent.id);
      setError(null);
      onMoved(res.data.lead_id, agent.name);
    } catch (e) {
      setError(apiError(e, 'Could not move this agent back to Leads. Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProposalDialog
      visible={visible && !!agent}
      onHide={close}
      title="Move back to Leads?"
      subtitle={agent ? <strong style={{ color: C.teal }}>{agent.name}</strong> : undefined}
      icon={ArrowLeftRight}
      busy={busy}
      maxWidth="460px"
      footer={
        <>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={close} disabled={busy} />
          <GlobalButton label={busy ? 'MOVING…' : 'MOVE TO LEADS'} icon={ArrowLeftRight} onClick={confirm} loading={busy} />
        </>
      }
    >
      {error && <Notice tone="error">{error}</Notice>}
      <p style={{ margin: 0, fontSize: '0.78rem', color: C.sub, lineHeight: 1.6 }}>
        {agent?.has_lead_history
          ? 'The lead comes back exactly as it was — status, notes, call history and proposals — and this agent record is removed.'
          : 'A new lead is created from this agent’s name, phone, email and city, and this agent record is removed. They were added as an agent directly, so there is no earlier lead history to bring back.'}
        {agent && agent.lead_count > 0 && (
          <span style={{ display: 'block', marginTop: '8px', color: '#c62828' }}>
            This agent still has {agent.lead_count} lead{agent.lead_count === 1 ? '' : 's'} on record, so the move will be refused until those are dealt with.
          </span>
        )}
      </p>
    </ProposalDialog>
  );
};

export default AgentToLeadModal;